import Modal from '../../Atoms/Modal/Modal';
import Buttons from '../../Atoms/Buttons/Buttons';
import styles from './ContactMessages.module.scss';

/**
 * One contact message, in full.
 *
 * STRUCTURE — header / body / footer, which is what makes a dialog legible at
 * any size: the title and close stay put, only the content between them
 * scrolls, and the actions stay reachable without scrolling to the bottom of a
 * long message.
 *
 * The shared `Modal` atom supplies the portal, the backdrop and the Escape
 * handler, all of which are worth keeping. It also renders its own close
 * control as `<img onClick>` — not focusable, not operable by keyboard — so
 * this dialog hides it (see `.details-modal` in the stylesheet) and puts a real
 * `<button>` in the header instead. The atom's fixed padding is reset for the
 * same reason: a header and footer have to reach the edges to read as bands.
 *
 * RTL: the title sits at the inline start (the right in Arabic) and the close
 * at the inline end, using logical properties throughout — this is reading
 * content, so it mirrors correctly in either direction.
 *
 * The sender's e-mail and phone are links: replying is the point of the screen,
 * and making someone copy an address out of a table is how replies get typed
 * wrong.
 *
 * `departmentName` arrives as a prop rather than being looked up here. The
 * department list is now served by `/api/ContactUs/Settings` and fetched once
 * by the page; resolving it again inside the dialog would mean a second
 * subscription to the same query for one string, and a dialog that renders a
 * different name from the row behind it while that query is still loading.
 */
const MessageDetailsModal = ({
  message,
  formatDate,
  departmentName,
  onClose,
  onDelete,
}) => {
  const rows = [
    // First: it is the identifier a visitor quotes when following up.
    {
      label: 'الرقم المرجعي',
      value:
        message?.referenceNumber != null ? `#${message.referenceNumber}` : '',
      ltr: true,
    },
    { label: 'المرسل', value: message?.fullName },
    { label: 'البريد الإلكتروني', value: message?.email, type: 'email' },
    { label: 'رقم الجوال', value: message?.mobile, type: 'tel' },
    {
      label: 'الجهة المعنية',
      value: departmentName || message?.receiverEmail,
    },
    { label: 'التاريخ', value: formatDate(message?.createdOn), ltr: true },
    {
      // What happened to THIS message when it arrived — not what the switch
      // says today, which may have been changed since.
      label: 'أُرسلت نسخة بالبريد',
      value: message?.isEmailSentRequired ? 'نعم' : 'لا',
    },
  ];

  const renderValue = (row) => {
    if (!row.value) return '—';
    if (row.type === 'email') {
      return (
        <a className={styles.ltr} href={`mailto:${row.value}`}>
          {row.value}
        </a>
      );
    }
    if (row.type === 'tel') {
      return (
        <a className={styles.ltr} href={`tel:${row.value}`}>
          {row.value}
        </a>
      );
    }
    return row.value;
  };

  return (
    <Modal
      onClose={onClose}
      closeWhite={false}
      innerContainerClass={styles['details-modal']}
      modalInnerContainerStyle={{ padding: 0, gap: 0 }}
      // Hide the atom's own close icon via the inline-style prop it exposes,
      // rather than a CSS selector: `Modal.jsx` renders it with a CSS-MODULE
      // class (`styles['close-icon']`), which the bundler hashes to something
      // like `Modal_close-icon__xxx`. A `:global(.close-icon)` rule in this
      // file's stylesheet can never match that hashed name, which is why the
      // black overlay icon kept showing up in the corner despite that rule.
      // `closeStyle` is spread onto the icon's own `style` attribute, so it
      // always wins regardless of class-name hashing.
      closeStyle={{ display: 'none' }}
    >
      <div className={styles['dialog']} dir='rtl'>
        <header className={styles['dialog-head']}>
          <div className={styles['dialog-head-text']}>
            <h2 className={styles['dialog-title']}>
              {message?.subject || 'رسالة'}
            </h2>
            <span
              className={
                message?.isRead ? styles['badge-read'] : styles.badge
              }
            >
              {message?.isRead ? 'مقروءة' : 'جديدة'}
            </span>
          </div>

          <button
            type='button'
            className={styles['dialog-close']}
            onClick={onClose}
            aria-label='إغلاق'
          >
            <span aria-hidden='true'>&#10005;</span>
          </button>
        </header>

        <div className={styles['dialog-body']}>
          <dl className={styles['details-grid']}>
            {rows.map((row) => (
              <div className={styles['details-row']} key={row.label}>
                <dt>{row.label}</dt>
                <dd className={row.ltr ? styles.ltr : undefined}>
                  {renderValue(row)}
                </dd>
              </div>
            ))}
          </dl>

          <div className={styles['details-body']}>
            <span className={styles['details-body-label']}>نص الرسالة</span>
            <p className={styles['details-message']}>
              {message?.message || '—'}
            </p>
          </div>
        </div>

        <footer className={styles['dialog-foot']}>
          {message?.email && (
            <Buttons
              text='الرد عبر البريد'
              rounded={true}
              color='rgba(13, 160, 110, 1)'
              handleOnClick={() => {
                // `Re:` so the reply threads against the original in the
                // recipient's client rather than arriving as a new topic.
                const subject = encodeURIComponent(
                  `Re: ${message?.subject || ''}`
                );
                window.location.href = `mailto:${message.email}?subject=${subject}`;
              }}
            />
          )}
          <Buttons
            text='حذف'
            rounded={true}
            color='rgba(214, 69, 65, 1)'
            handleOnClick={onDelete}
          />
        </footer>
      </div>
    </Modal>
  );
};

export default MessageDetailsModal;
